import React, { useMemo, useState } from 'react';

import { Button, useNavPersistedState } from '../Navigation';
import { HStack, LinkDivider, SearchInput, VStack } from '../../core-ui';
import { confirm } from '../App/AppDialog';
import formatDate from '../../core/support/formatDate';
import { TableView } from '../../core-ui/TableView';

import { Email } from './types';
import { EmailListRow, useRowContext } from './EmailListRow';

function RowItemActionList() {
  const {
    email,
    deleteEmail,
    isDeleting,
    duplicateEmail,
    isDuplicating,
  } = useRowContext();
  return (
    <HStack justify="flex-end" divider={<LinkDivider />}>
      <Button
        variant="link"
        isDisabled={isDeleting}
        isLoading={isDuplicating}
        onClick={() => {
          duplicateEmail();
        }}
      >
        {t('Duplicate')}
      </Button>
      <Button
        variant="link"
        isDisabled={isDuplicating}
        isLoading={isDeleting}
        onClick={async () => {
          const proceed = await confirm(
            t('Delete email?'),
            t('This will permanently delete the email "{subject}"', {
              subject: email.subject,
            }),
          );
          if (proceed) {
            deleteEmail();
          }
        }}
      >
        {t('Delete')}
      </Button>
    </HStack>
  );
}

export default function EmailListView(props: {
  /** A unique key which we'll use to store page-wide state */
  storeKey: string;
  data: Array<Email>;
  onEmailClick: (email: Email) => void;
  onEmailDuplicated: (email: Email) => void;
  showRecipientCount?: boolean;
}) {
  let {
    storeKey,
    data,
    onEmailClick,
    onEmailDuplicated,
    showRecipientCount,
  } = props;
  let [filterText, setFilterText] = useState('');
  const [sortBy, setSortBy] = useNavPersistedState<[number, boolean]>(
    storeKey + '-sort-state',
    // Initially sort by column 2 (created) descending
    [2, true],
  );

  const filteredEmails = useMemo(() => {
    return filterEmails(data, filterText);
  }, [data, filterText]);

  return (
    <VStack flex={1} align="stretch">
      <HStack px={1} pt={4}>
        <SearchInput
          onChange={(event) => setFilterText(event.target.value)}
          onClear={() => setFilterText('')}
          value={filterText}
        />
      </HStack>
      <TableView
        data={filteredEmails}
        renderRow={(email, render) => (
          <EmailListRow
            email={email}
            onClick={onEmailClick}
            render={render}
            onEmailDuplicated={() => onEmailDuplicated(email)}
          />
        )}
        sortBy={sortBy}
        onSortChange={setSortBy}
        columns={[
          {
            title: t('Email Subject'),
            render: (email) => email.subject,
            tdProps: { overflow: 'hidden', overflowWrap: 'break-word' },
            sortable: {
              sortFn: (a, b) => a.subject.localeCompare(b.subject),
            },
          },
          showRecipientCount
            ? {
                title: t('Recipients'),
                render: (email) => email.recipientCount,
                thProps: { width: '1%' },
                tdProps: { textAlign: 'right' },
                sortable: {
                  sortFn: (a, b) => a.recipientCount - b.recipientCount,
                },
              }
            : {
                title: '',
                render: (_email) => null,
                tdProps: { width: 0 },
              },
          {
            title: t('Created'),
            render: (email) => formatDate(email.createdAt),
            tdProps: { whiteSpace: 'nowrap' },
            sortable: {
              sortFn: (a, b) => {
                return Date.parse(a.createdAt) - Date.parse(b.createdAt);
              },
            },
          },
          {
            title: t('Last Modified'),
            render: (email) => formatDate(email.updatedAt),
            tdProps: { whiteSpace: 'nowrap' },
            sortable: {
              sortFn: (a, b) => {
                return Date.parse(a.updatedAt) - Date.parse(b.updatedAt);
              },
            },
          },
          {
            title: '',
            render: (email) => <RowItemActionList key={email.id} />,
            tdProps: { width: '1%', cursor: 'default' },
            onClick: (event) => event.stopPropagation(),
          },
        ]}
      />
    </VStack>
  );
}

function filterEmails(emails: Array<Email>, searchText: string) {
  const searchTextLower = searchText.toLowerCase().trim();
  return emails.filter((email) => matchesSearch(email, searchTextLower));
}

function matchesSearch(email: Email, searchText: string) {
  if (searchText === '') {
    return true;
  }
  for (const key of ['id', 'subject'] as const) {
    const value = typeof email[key] === 'string' ? email[key] : '';
    if (value.toLowerCase().includes(searchText)) {
      return true;
    }
  }
  return false;
}
